// IND-025: «Lo importante» del detalle del material. Como máximo 4 frases,
// en este orden de prioridad (solo las que aplican):
//  1) rotura con el ritmo habitual al menos 2 días laborables antes que con
//     las OF (dentro de las 8 semanas de la previsión);
//  2) compra pendiente que llega después de la primera rotura;
//  3) consumo de las últimas 4 semanas ±25 % sobre la media de 12 meses;
//  4) rotación de los últimos 3 meses ±25 % frente a los 3 anteriores;
//  5) sin consumo en 8 semanas con stock > 0;
//  6) los 3 últimos recuentos con el mismo signo y cada uno > 2 %;
//  7) entradas ÷ consumo de 3 meses < 0,8 o > 1,25.
// 3 a 7 los decide el backend (detail.highlights); aquí solo se redactan.
import type { MaterialDetail } from '../services/industrialCostService';
import type { MaterialStockProjectionRow } from '../types/industrialCost';
import type { MaterialForecast } from './materialForecast';
import { addDaysKey, isWorkday } from './materialForecast';
import { esNumberFormat } from './numberFormat';

export const MAX_HIGHLIGHTS = 4;
const MIN_WORKDAYS_AHEAD = 2;

const qty = esNumberFormat({ maximumFractionDigits: 1 });
const pct = esNumberFormat({ maximumFractionDigits: 0 });
const ratio = esNumberFormat({ minimumFractionDigits: 1, maximumFractionDigits: 2 });

function dayMonth(value: string): string {
  const [, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}`;
}

function signedPct(value: number): string {
  const text = `${pct.format(Math.abs(value))} %`;
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text;
}

function workdaysBetween(from: string, to: string, holidays: ReadonlySet<string>): number {
  // Días laborables desde «from» (incluido) hasta «to» (excluido).
  let total = 0;
  for (let day = from.slice(0, 10); day < to.slice(0, 10); day = addDaysKey(day, 1)) {
    if (isWorkday(day, holidays)) total += 1;
  }
  return total;
}

function calendarDays(from: string, to: string): number {
  const start = new Date(`${from.slice(0, 10)}T12:00:00`).getTime();
  const end = new Date(`${to.slice(0, 10)}T12:00:00`).getTime();
  return Math.round((end - start) / 86_400_000);
}

export function buildMaterialHighlights({
  material,
  detail,
  forecast,
  unit,
}: {
  material: Pick<MaterialStockProjectionRow, 'calculated_stock_qty' | 'first_shortage_date' | 'purchase_events'>;
  detail: Pick<MaterialDetail, 'highlights' | 'upcoming_holidays'> & {
    entries?: Pick<MaterialDetail['entries'], 'period_from'>;
  };
  forecast: Pick<MaterialForecast, 'rhythmShortageDate' | 'ofShortageDate'> | null;
  unit: string;
}): string[] {
  const out: string[] = [];
  const holidays = new Set(detail.upcoming_holidays.map((day) => day.slice(0, 10)));
  const unitText = unit ? ` ${unit}` : '';

  // 1) Rotura con el ritmo habitual antes que con las OF.
  const rhythmDate = forecast?.rhythmShortageDate ?? null;
  if (rhythmDate) {
    const ofDate = forecast?.ofShortageDate ?? material.first_shortage_date?.slice(0, 10) ?? null;
    if (!ofDate) {
      out.push(
        `Con el ritmo habitual se queda sin stock el ${dayMonth(rhythmDate)}; con solo las OF no hay rotura en 8 semanas.`,
      );
    } else {
      const ahead = workdaysBetween(rhythmDate, ofDate, holidays);
      if (ahead >= MIN_WORKDAYS_AHEAD) {
        out.push(
          `Con el ritmo habitual se queda sin stock el ${dayMonth(rhythmDate)}, ${ahead} días laborables antes que con las OF (${dayMonth(ofDate)}).`,
        );
      }
    }
  }

  // 2) Compra pendiente que llega después de la primera rotura.
  const shortage = material.first_shortage_date?.slice(0, 10) ?? null;
  if (shortage) {
    const late = material.purchase_events
      .filter(
        (event) =>
          event.expected_delivery_date &&
          event.purchase_status !== 'overdue' &&
          event.expected_delivery_date.slice(0, 10) > shortage,
      )
      .sort((a, b) => (a.expected_delivery_date ?? '').localeCompare(b.expected_delivery_date ?? ''))[0];
    if (late?.expected_delivery_date) {
      const days = calendarDays(shortage, late.expected_delivery_date);
      out.push(
        `El pedido ${late.purchase_order_number || 'pendiente'} llega el ${dayMonth(late.expected_delivery_date)}, ${days} ${days === 1 ? 'día' : 'días'} tarde: la rotura es el ${dayMonth(shortage)}.`,
      );
    }
  }

  const signals = detail.highlights;

  // 3) Cambio de consumo y el producto que lo explica.
  const change = signals.consumption_change;
  if (change) {
    const product = change.product;
    const productText = product
      ? `; lo explica sobre todo ${product.product_code || product.product_name || 'un producto'}${product.is_new ? ' (nuevo)' : ''}`
      : '';
    out.push(
      `El consumo de las últimas 4 semanas va ${signedPct(change.pct)} sobre la media de 12 meses por día laborable${productText}.`,
    );
  }

  // 4) Rotación distinta de la de los 3 meses anteriores.
  const rotation = signals.rotation_change;
  if (rotation) {
    out.push(
      `La rotación de los últimos 3 meses es de ${qty.format(rotation.recent_days)} días, un ${pct.format(Math.abs(rotation.pct))} % más ${rotation.pct > 0 ? 'lenta' : 'rápida'} que en los 3 anteriores (${qty.format(rotation.previous_days)} días).`,
    );
  }

  // 5) Parado con stock.
  const stock = material.calculated_stock_qty;
  if (signals.idle && stock !== null && stock > 0) {
    const last = signals.idle.last_consumption_date;
    out.push(
      last
        ? `Parado desde el ${dayMonth(last)}: sin consumo en 8 semanas y ${qty.format(stock)}${unitText} en stock.`
        : `Sin consumo en los partes y ${qty.format(stock)}${unitText} en stock.`,
    );
  }

  // 6) Recuentos sesgados.
  const bias = signals.counts_bias;
  if (bias) {
    out.push(
      `Los 3 últimos recuentos han dado ${bias.direction === 'more' ? 'más' : 'menos'} de lo calculado (${bias.pcts.map(signedPct).join(', ')}).`,
    );
  }

  // 7) Entradas frente a consumo.
  const entries = signals.entries_ratio;
  if (entries) {
    out.push(
      // Decisión 29/10: el mismo periodo que la rotación.
      `${detail.entries?.period_from ? `Desde el ${dayMonth(detail.entries.period_from)}` : 'En 3 meses'} ha entrado ${ratio.format(entries.ratio)} veces lo consumido: entra ${entries.direction === 'low' ? 'menos' : 'más'} de lo que se gasta.`,
    );
  }

  return out.slice(0, MAX_HIGHLIGHTS);
}
